import { ActionMode, GeneralStatusEnum, KindergartenDetailsTabs } from '../../../common/enums/common.enum';
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Link, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import { createNotification, getStatusColor } from '../../../utilities/helper/notification.helper';
import { CustomErrorStatus, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createParent, fetchChildrenBasicInfo, getParentDetails, updateParent } from '../../../api/kindergarten.api';
import { ChildBasicInfo, CreateParentDto, ParentDto, UpdateParentDto } from '../../../common/interfaces/kindergarten.interface';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as CircleIcon } from '../../../assets/circle-icon.svg';
import GenericPeopleDropdown from '../../../components/GenericPeopleDropdown';
import GenericInfoModal from '../../../components/GenericInfoModal';
import { ProfilePageBox, ProfilePictureAvatar } from '../../../common/styles/Common.styles';
import ParentBillingSection from './ParentBillingSection';
import { FeatureCode } from '../../../common/enums/common.enum';
import { useKindergartenFeatures } from '../../../utilities/hooks/useKindergartenFeatures';
import { useTranslation } from 'react-i18next';

interface ParentPageProps {
  mode: ActionMode;
}

const ParentPage: React.FC<ParentPageProps> = ({ mode }) => {
  const { t } = useTranslation();
  const { parentId, kindergartenId, kindergartenName } = useParams<{
    parentId?: string;
    kindergartenId: string;
    kindergartenName: string;
  }>();

  const navigate = useNavigate();
  const { isEnabled } = useKindergartenFeatures(kindergartenId);
  const isInvoicingEnabled = isEnabled(FeatureCode.INVOICING);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<GeneralStatusEnum>(GeneralStatusEnum.ACTIVE);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [children, setChildren] = useState<ChildBasicInfo[]>([]);
  const [childrenOptions, setChildrenOptions] = useState<ChildBasicInfo[]>();
  const [initialParent, setInitialParent] = useState<ParentDto>();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [pendingChildren, setPendingChildren] = useState<ChildBasicInfo[]>([]);
  const [isEditing, setIsEditing] = useState(mode === ActionMode.CREATE || mode === ActionMode.EDIT);
  const [currentMode, setCurrentMode] = useState<ActionMode>(mode);

  useEffect(() => {
    getChildren();
  }, [kindergartenId]);

  useEffect(() => {
    if (childrenOptions) {
      if (currentMode === ActionMode.EDIT || (currentMode === ActionMode.DETAILS && parentId)) {
        fetchParentDetails();
      }
    }
  }, [kindergartenId, mode, parentId, childrenOptions]);

  const handleSave = async () => {
    const success = await saveParent();
    if (success) {
      navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.Parents}`);
    }
  };

  const getChildren = async () => {
    try {
      if (!kindergartenId) return;
      const response = await fetchChildrenBasicInfo(kindergartenId);
      setChildrenOptions(response.results ?? []);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('children.loadFailed'));
    }
  };

  const handleSaveAndCreateAnother = async () => {
    const success = await saveParent();
    if (success) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setStatus(GeneralStatusEnum.ACTIVE);
      setChildren([]);
    }
  };

  const saveParent = async () => {
    if (!kindergartenId) return;

    if (!firstName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, t('people.validation.firstNameRequired'));
      return false;
    }
    if (!lastName.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, t('people.validation.lastNameRequired'));
      return false;
    }

    if (currentMode === ActionMode.CREATE) {
      if (!email.trim()) {
        createNotification(MESSAGE_TYPE.ERROR, t('people.validation.emailRequired'));
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        createNotification(MESSAGE_TYPE.ERROR, t('people.validation.emailInvalid'));
        return false;
      }
    }
    try {
      if (currentMode === ActionMode.CREATE) {
        const parentPayload: CreateParentDto = {
          email: email,
          firstName: firstName,
          lastName: lastName,
          phoneNumber: phone,
          status: status
        };
        if (children.length > 0) {
          parentPayload.childIds = children.map(c => c.id);
        }
        await createParent(kindergartenId, parentPayload);
        createNotification(MESSAGE_TYPE.SUCCESS, t('parents.created'));
      } else if (currentMode === ActionMode.EDIT && parentId) {
        const parentPayload: UpdateParentDto = {};
        if (firstName !== initialParent?.firstName) parentPayload.firstName = firstName;
        if (lastName !== initialParent?.lastName) parentPayload.lastName = lastName;
        if (phone !== initialParent?.phoneNumber) parentPayload.phoneNumber = phone;
        if (status !== initialParent?.status) parentPayload.status = status;
        const initialChildIds = initialParent?.children?.map(c => c.id) || [];
        const currentChildIds = children.map(c => c.id);
        const childrenChanged =
          initialChildIds.length !== currentChildIds.length || !initialChildIds.every(id => currentChildIds.includes(id));

        if (childrenChanged) {
          parentPayload.childIds = currentChildIds;
        }
        if (Object.keys(parentPayload).length > 0) {
          await updateParent(kindergartenId, parentId, parentPayload);
          createNotification(MESSAGE_TYPE.SUCCESS, t('parents.updated'));
        }
      }
      return true;
    } catch (error: any) {
      const backendStatus = error?.response?.data?.status;
      if (backendStatus === CustomErrorStatus.USER_EMAIL_ALREADY_EXISTS) {
        createNotification(MESSAGE_TYPE.ERROR, t('people.emailAlreadyRegistered'));
      } else {
        const errorMessage = error?.response?.data?.message ?? t('parents.saveFailed');
        createNotification(MESSAGE_TYPE.ERROR, errorMessage);
      }
      return false;
    }
  };
  const fetchParentDetails = async () => {
    if (!kindergartenId || !parentId) return;
    try {
      const response = await getParentDetails(kindergartenId, parentId);
      if (response.results) {
        const parent = response.results;
        setInitialParent(parent);
        setFirstName(parent.firstName);
        setLastName(parent.lastName);
        setEmail(parent.email);
        setPhone(parent.phoneNumber ?? '');
        setStatus(parent.status);
        setAvatarUrl(parent.profileImage ?? null);

        if (parent.children?.length) {
          const mappedChildren = parent.children.map(c => childrenOptions?.find(co => co.id === c.id) || c);
          setChildren(mappedChildren);
        } else {
          setChildren([]);
        }
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('parents.detailsLoadFailed'));
    }
  };

  const getPageTitle = () => {
    switch (currentMode) {
      case ActionMode.CREATE:
        return t('parents.create');
      case ActionMode.EDIT:
        return t('parents.edit');
      case ActionMode.DETAILS:
        return `${firstName} ${lastName}`;
    }
  };

  const handleChildrenChange = (newChildren: ChildBasicInfo[]) => {
    const removed = children.filter(c => !newChildren.some(nc => nc.id === c.id));
    if (removed.length > 0) {
      setPendingChildren(newChildren);
      setOpenConfirmModal(true);
    } else {
      setChildren(newChildren);
    }
  };

  const confirmRemoveChildren = () => {
    setChildren(pendingChildren);
    setPendingChildren([]);
    setOpenConfirmModal(false);
  };

  const cancelRemoveChildren = () => {
    setPendingChildren([]);
    setOpenConfirmModal(false);
  };

  const isFormValid = useMemo(() => {
    return Boolean(firstName.trim() && lastName.trim() && email.trim());
  }, [firstName, lastName, email]);

  return (
    <Box p={7}>
      <Breadcrumbs aria-label={t('common.breadcrumb')} sx={{ mb: 3 }}>
        <Link color="text.secondary" underline="hover" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          {t('nav.kindergartens')}
        </Link>
        {kindergartenName && (
          <Link
            color="text.secondary"
            underline="hover"
            onClick={() => navigate(`/${kindergartenId}/details?tab=${KindergartenDetailsTabs.Parents}`)}
            sx={{ cursor: 'pointer' }}
          >
            {kindergartenName}
          </Link>
        )}
        <Typography color="text.primary">{getPageTitle()}</Typography>
      </Breadcrumbs>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="44px">
        <Typography variant="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {currentMode === ActionMode.DETAILS && (
            <CircleIcon
              style={{
                color: getStatusColor(status),
                fontSize: 12
              }}
            />
          )}
          {currentMode === ActionMode.CREATE && t('parents.create')}
          {currentMode === ActionMode.EDIT && t('parents.edit')}
          {currentMode === ActionMode.DETAILS && `${firstName} ${lastName}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {mode === ActionMode.CREATE && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Secondary}
              variant="outlined"
              onClick={handleSaveAndCreateAnother}
              label={t('common.saveAndCreateAnother')}
              disabled={!isFormValid}
            />
          )}
          {isEditing && currentMode !== ActionMode.DETAILS && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={handleSave}
              label={t('common.save')}
              disabled={!isFormValid}
            />
          )}
          {currentMode === ActionMode.DETAILS && !isEditing && (
            <GenericButton
              buttonType={GenericButtonTypeEnum.Primary}
              variant="contained"
              onClick={() => {
                setIsEditing(true);
                setCurrentMode(ActionMode.EDIT);
              }}
              label={t('common.edit')}
              startIcon={<EditIcon />}
            />
          )}
        </Box>
      </Box>

      <ProfilePageBox>
        {(mode === ActionMode.EDIT || mode === ActionMode.DETAILS) && (
          <ProfilePictureAvatar src={avatarUrl ?? undefined} alt={`${firstName} ${lastName}`} />
        )}
        <Box
          sx={{
            display: 'flex',
            width: '500px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '20px'
          }}
        >
          <GenericTextField
            label={t('people.firstName')}
            required={true}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label={t('people.lastName')}
            required={true}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            disabled={!isEditing}
          />
          <GenericTextField
            label={t('common.email')}
            required={true}
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={mode !== ActionMode.CREATE}
          />

          <GenericTextField label={t('common.phone')} required={false} value={phone} onChange={e => setPhone(e.target.value)} disabled={!isEditing} />
          <GenericStatusSwitch label={t('parents.status')} status={status} onChange={newStatus => setStatus(newStatus)} disabled={!isEditing} />
          {childrenOptions && (
            <GenericPeopleDropdown
              label={t('kindergarten.tabs.children')}
              options={childrenOptions}
              selectedOptions={children}
              onChange={handleChildrenChange}
              disabled={!isEditing}
              isChildren={true}
            />
          )}
        </Box>
      </ProfilePageBox>

      {/* only on an existing parent, and only where the kindergarten invoices at all */}
      {mode === ActionMode.DETAILS && parentId && kindergartenId && isInvoicingEnabled && (
        <ParentBillingSection
          kindergartenId={kindergartenId}
          parentId={parentId}
          kindergartenName={kindergartenName ?? ''}
        />
      )}

      <GenericInfoModal
        open={openConfirmModal}
        onClose={cancelRemoveChildren}
        onConfirm={confirmRemoveChildren}
        label={t('parents.removeChildren')}
        message={t('parents.removeChildrenConfirm', { name: `${firstName} ${lastName}` })}
        confirmLabel={t('common.remove')}
        entityName=""
      />
    </Box>
  );
};
export default ParentPage;
